// src/components/IdeaCard.jsx
import React from 'react';
import { CheckSquare, Book, ExternalLink, FileEdit, Sparkles } from 'lucide-react';
import StarRating from './StarRating';

function IdeaCard({ idea, onClick }) {
  const completedCount = idea.subtasks?.filter(t => t.completed).length || 0;
  const totalCount = idea.subtasks?.length || 0;

  const statusColors = {
    'Drafted': 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    'In Progress': 'bg-blue-200 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
    'Completed': 'bg-green-200 dark:bg-green-900 text-green-800 dark:text-green-200',
    'Paused': 'bg-yellow-200 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
    'Archived': 'bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
  };

  const statusColor = statusColors[idea.status] || statusColors['Drafted'];
  const hasLessonsLearned = idea.lessonsLearned && idea.lessonsLearned.trim().length > 0;
  const resourcesCount = idea.resources?.length || 0;
  const hasResources = resourcesCount > 0;

  // Development status - default to 'raw' for existing ideas without this field
  const developmentStatus = idea.developmentStatus || 'raw';
  const isRaw = developmentStatus === 'raw';

  // Check if idea is "new" (created within the last 5 minutes)
  const isNew = (() => {
    if (!idea.createdAt) return false;
    const createdTime = idea.createdAt.toDate ? idea.createdAt.toDate() : new Date(idea.createdAt);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return createdTime > fiveMinutesAgo;
  })();

  // Get card color styling
  const cardColor = idea.color || null;
  
  // Build border style: combine color border with development status border
  const borderStyle = {};
  if (cardColor) {
    borderStyle.borderLeft = `4px solid ${cardColor}`;
  }
  
  // Visual distinction for raw vs developed
  const cardClasses = `bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all ${
    isRaw 
      ? 'border-2 border-dashed border-gray-300 dark:border-gray-600' 
      : 'border border-solid border-gray-200 dark:border-gray-700'
  }`;

  return (
    <div
      onClick={onClick}
      className={cardClasses}
      style={borderStyle}
    >
      {/* Title Row with NEW badge */}
      <div className="flex items-start gap-2 mb-2">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate flex-1">
          {idea.idea}
        </h3>
        {isNew && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 text-white animate-pulse shrink-0">
            <Sparkles className="w-3 h-3" />
            NEW
          </span>
        )}
      </div>

      {/* Status Badge + Star Rating Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
            {idea.status}
          </span>
          {/* Development Status Badge */}
          {isRaw ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" title="Not yet developed with AI">
              <FileEdit className="w-3 h-3" />
              Draft
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-300" title={`Enhanced with AI (${idea.developmentPath || 'AI'})`}>
              <Sparkles className="w-3 h-3" />
              AI
            </span>
          )}
          {hasLessonsLearned && (
            <Book className="w-4 h-4 text-amber-600 dark:text-amber-400" title="Lessons learned captured" />
          )}
          {hasResources && (
            <div className="flex items-center gap-1" title={`${resourcesCount} resource${resourcesCount > 1 ? 's' : ''} attached`}>
              <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{resourcesCount}</span>
            </div>
          )}
        </div>
        <StarRating value={idea.rating} readOnly={true} size={5} />
      </div>

      {/* Task Counter */}
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <CheckSquare className="w-4 h-4" />
        <span>
          {completedCount}/{totalCount} tasks
        </span>
      </div>
    </div>
  );
}

export default IdeaCard;

